import type {
  SeatSlice,
  Person,
  SeatSegment,
} from "@b2bportal/air-booking-api";
import type { TripSlice, Airport } from "@b2bportal/air-shopping-api";
import { AirlineIcon } from "@b2bportal/core-ui";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Divider } from "@hopper-b2b/ui";
import { removeTimezone } from "@hopper-b2b/utilities";
import { formatDateTime, DateTimeFormatStyle } from "@hopper-i18n/formatter";
import { SeatSelections } from "../SeatSelections";
import type { SelectedSeatSegment } from "../SelectedSeats";

import styles from "./styles.module.scss";

export const FlightSliceRow = ({
  slice,
  seatSlice,
  airports,
  selectedSeatSegments,
  passengers,
  onEdit,
}: {
  slice: TripSlice;
  seatSlice: SeatSlice;
  airports: { [key: string]: Airport };
  selectedSeatSegments: SelectedSeatSegment[];
  passengers: Person[];
  onEdit: (seatSegment: SeatSegment) => void;
}) => {
  const { t, locale } = useI18nContext();

  // Defensive check - if seatSlice or segments is undefined, don't crash
  if (!seatSlice || !seatSlice.segments) {
    return null;
  }

  return (
    <div className={styles.FlightSliceRow}>
      {slice.segmentDetails.map((seg, i: number) => {
        const segment = seatSlice.segments[i];

        // Skip this segment if seat data is missing
        if (!segment) {
          return null;
        }

        const seatAvailable = segment.seatsAvailable;
        const handleEdit = () => {
          onEdit(segment);
        };

        const departureDate = formatDateTime(
          new Date(removeTimezone(seg.departureTime)),
          locale,
          DateTimeFormatStyle.ShortMonthDayShortWeekday
        );

        const isSeatSelectedForSegment = selectedSeatSegments.find(
          (seatSegment) =>
            seatSegment.originCode === seg.originCode &&
            seatSegment.destinationCode === seg.destinationCode
        );

        return (
          <>
            <Divider className={styles.Break} />
            <div className="segment-row">
              <div className="left">
                <AirlineIcon className="icon" airlineCode={seg.airlineCode} />
                <div className="content">
                  {airports[seg.originCode]?.cityName &&
                    airports[seg.destinationCode]?.cityName && (
                      <p className="route">
                        {t("seats.cityToCity", {
                          originCity: airports[seg.originCode]?.cityName,
                          destinationCity:
                            airports[seg.destinationCode]?.cityName,
                        })}
                      </p>
                    )}
                  <p className="date">{departureDate}</p>
                  <p className="airline">{seg.airlineName}</p>
                </div>
              </div>
              {seatAvailable ? (
                <button onClick={handleEdit}>
                  {isSeatSelectedForSegment ? t("edit") : t("choose")}
                </button>
              ) : null}
            </div>
            {!seatAvailable ? (
              <p className="unavailable">{t("seats.unavailableWarning")}</p>
            ) : (
              <>
                <Divider className={styles.Break} />
                <SeatSelections
                  selectedSeatSegments={selectedSeatSegments}
                  passengers={passengers}
                  destinationCode={seg.destinationCode}
                  originCode={seg.originCode}
                />
              </>
            )}
          </>
        );
      })}
    </div>
  );
};
